import type {Routes} from "@angular/router";
import {allEqualsOrdered, equals, type IEquatable} from "./Equatable";

export type RoutePathParameter = string | number;
// export type RoutePathComponent = string | RoutePathParameter;
// export type RouterAction = string | RoutePathComponent[];

// We currently require routes be rooted, since we're not aware of any relative routes in static typing.
type RootRoutePath<Path extends string> = `/${Path}`;

type ParseFirstSegment<Segment extends string, ParamsType> = Segment extends `:${string}` ? ParamsType : RootRoutePath<Segment>;
type ParseRestSegment<Segment extends string, ParamsType> = Segment extends `:${string}` ? ParamsType : Segment;

type SplitRest<RestPath extends string, ParamsType> = RestPath extends `${infer Segment}/${infer Next}`
  ? [ParseRestSegment<Segment, ParamsType>, ...SplitRest<Next, ParamsType>]
  : [ParseRestSegment<RestPath, ParamsType>];

type PathSegments<Path extends string, ParamsType> = Path extends `${infer First}/${infer Rest}`
  ? [ParseFirstSegment<First, ParamsType>, ...SplitRest<Rest, ParamsType>]
  : [ParseFirstSegment<Path, ParamsType>];

type ExtractParamNames<Path extends string> = Path extends `${string}:${infer Param}/${infer Rest}`
  ? Param | ExtractParamNames<`/${Rest}`>
  : Path extends `${string}:${infer Param}`
    ? Param
    : never;
type NonParameterizedPathString<Path extends string> = ExtractParamNames<Path> extends never ? RootRoutePath<Path> : never;

type JoinPaths<Parent extends string, Child extends string> = Parent extends ""
  ? Child
  : Child extends ""
    ? Parent
    : `${Parent}/${Child}`;

type FlattenRoutes<R extends Routes, ParentPath extends string = ""> = {
  [K in keyof R]: R[K] extends { path: infer Path extends string, children?: infer Children }
    ? (
      | (Path extends "**" ? never : JoinPaths<ParentPath, Path>)
      | (Children extends Routes
      ? FlattenRoutes<Children, JoinPaths<ParentPath, Path>>
      : never)
      )
    : never
}[number];

type FlattenNonParameterizedRoutes<R extends Routes, ParentPath extends string = ""> = {
  [K in keyof R]: R[K] extends { path: infer Path extends string, children?: infer Children }
    ? ExtractParamNames<Path> extends never ?
      (
        | (Path extends "**" ? never : JoinPaths<ParentPath, Path>)
        | (Children extends Routes
        ? FlattenNonParameterizedRoutes<Children, JoinPaths<ParentPath, Path>>
        : never)
        )
      : never
    : never
}[number];

export type RouteSegments<R extends Routes, ParamsType = RoutePathParameter> =
  PathSegments<FlattenRoutes<R>, ParamsType>
  | NonParameterizedPathString<FlattenNonParameterizedRoutes<R>>;

export function areEqualRouteSegments<R extends Routes, P extends IEquatable | RoutePathParameter>(
  lhs: Readonly<RouteSegments<R, P>>,
  rhs: Readonly<RouteSegments<R, P>>,
): boolean {
  const lhsArray = Array.isArray(lhs) ? lhs : [lhs];
  const rhsArray = Array.isArray(rhs) ? rhs : [rhs];
  return allEqualsOrdered(lhsArray, rhsArray, equals);
}
